import React from 'react';

function useReveal() {
  React.useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    // Add pre-reveal AFTER mount so the page has a visible fallback if JS fails
    els.forEach((el) => el.classList.add('pre-reveal'));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
}

/* ====== Nav ====== */
function Nav() {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-[100] py-[18px] px-[clamp(20px,4vw,64px)] flex justify-between items-center backdrop-blur-xl backdrop-saturate-150 transition-all duration-300
      ${scrolled ? 'bg-bg/75 border-b border-borderc' : 'bg-bg/55 border-b border-transparent'}`}>
      <a href="#home" className="flex items-center gap-2.5 font-mono text-[13px] tracking-wider">
        <span className="w-2 h-2 rounded-full bg-accent shadow-[0_0_12px_rgba(212,255,63,0.35)] anim-pulse-dot"></span>
        <span>ADAM.HANAFI / FULLSTACK</span>
      </a>
      <div className="hidden md:flex gap-7 font-mono text-[13px] text-ink-dim">
        <a href="#about"      className="lnk">About</a>
        <a href="#work"       className="lnk">Work</a>
        <a href="#experience" className="lnk">Experience</a>
        <a href="#awards"     className="lnk">Awards</a>
        <a href="#contact"    className="lnk">Contact</a>
      </div>
      <a href="#contact" className="group font-mono text-[12px] px-4 py-[9px] border border-borderc-strong rounded-full inline-flex items-center gap-2 transition-all duration-200 hover:bg-accent hover:text-black hover:border-accent">
        <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>
        Available for work
      </a>
    </nav>
  );
}

/* ====== Marquee ====== */
function Marquee() {
  const items = ['Fullstack', 'Mobile', 'Augmented Reality', 'Virtual Reality', 'E-commerce', 'Web Apps', 'POS Systems', 'Digital Signage'];
  const Row = () => (
    <span className="inline-flex items-center gap-14 pr-14">
      {items.map((t, i) => (
        <React.Fragment key={i}>
          <span>{t}</span>
          <span className="text-accent">✦</span>
        </React.Fragment>
      ))}
    </span>
  );
  return (
    <div className="border-y border-borderc bg-elev py-[26px] overflow-hidden whitespace-nowrap relative" aria-hidden="true">
      <div className="anim-marquee inline-flex font-display text-[clamp(32px,5vw,56px)] font-medium tracking-tight text-ink-dim">
        <Row /><Row />
      </div>
    </div>
  );
}

/* ====== Section wrappers ====== */
function SectionLabel({ children }) {
  return (
    <div className="inline-flex items-center gap-2.5 font-mono text-[12px] tracking-[0.18em] uppercase text-ink-dim mb-7">
      <span className="w-7 h-px bg-accent"></span>
      <span>{children}</span>
    </div>
  );
}
function SectionTitle({ children, className = '' }) {
  return (
    <h2 className={`h-display text-[clamp(40px,6vw,72px)] tracking-tighter2 leading-[1] m-0 mb-6 text-balance ${className}`}>
      {children}
    </h2>
  );
}
function SectionHeader({ label, title, counter }) {
  return (
    <div className="reveal flex justify-between items-end gap-8 flex-wrap mb-14">
      <div>
        <SectionLabel>{label}</SectionLabel>
        <SectionTitle>{title}</SectionTitle>
      </div>
      {counter && <div className="font-mono text-[12px] text-ink-muted tracking-wider">{counter}</div>}
    </div>
  );
}

/* ====== About ====== */
function About() {
  return (
    <section id="about" className="py-[clamp(96px,14vh,160px)]">
      <div className="max-w-[1320px] mx-auto px-[clamp(20px,4vw,64px)]">
        <div className="grid grid-cols-1 md:grid-cols-[1.05fr_1fr] gap-[clamp(40px,6vw,96px)] items-start">
          <div className="reveal">
            <SectionLabel>About</SectionLabel>
            <div className="space-y-4">
              <p className="text-[clamp(18px,1.5vw,22px)] leading-snug tracking-tight text-pretty">
                I'm a <span className="text-accent">fullstack &amp; mobile developer</span> based in Yogyakarta, Indonesia.
                Six plus years shipping production software — from Android apps in Java to e‑commerce platforms,
                AR / VR experiences, POS systems, and digital signage used across Asia.
              </p>
              <p className="text-[clamp(18px,1.5vw,22px)] leading-snug tracking-tight text-pretty">
                I work across the stack and across borders — currently at <span className="text-accent">Riglobe Pte. Ltd. Singapore</span> as
                a Mobile Developer, after stints in frontend, project management, and freelance.
                I've led teams, won government investment twice, and represented work at HANNOVER MESSE.
              </p>
            </div>
          </div>

          <div className="reveal rd-2">
            <div className="grid grid-cols-2 gap-px bg-borderc border border-borderc rounded-md overflow-hidden">
              <Stat num="6" sup="+"  label="Years building" />
              <Stat num="20" sup="+" label="Shipped projects" />
              <Stat num="8"          label="Awards & nominations" />
              <Stat num="∞"          label="Coffee consumed" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
function Stat({ num, sup, label }) {
  return (
    <div className="bg-elev p-7 flex flex-col gap-2 transition-colors hover:bg-surface">
      <div className="h-display text-[clamp(36px,4vw,52px)] tracking-tighter2 leading-none">
        {num}{sup && <sup className="text-[0.45em] text-accent ml-0.5 align-top relative top-[0.4em]">{sup}</sup>}
      </div>
      <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-dim">{label}</div>
    </div>
  );
}

/* ====== Skills ====== */
function Skills() {
  const skills = [
    { icon: 'Jv',  name: 'Java',               meta: 'Android · Backend' },
    { icon: 'JS',  name: 'JavaScript',         meta: 'React · Node' },
    { icon: 'Tw',  name: 'Tailwind',           meta: 'Modern CSS' },
    { icon: 'PH',  name: 'PHP',                meta: 'CMS · APIs' },
    { icon: 'SQ',  name: 'SQL',                meta: 'MySQL · Room' },
    { icon: '< >', name: 'HTML / CSS',         meta: 'Markup · Layout' },
    { icon: 'AR',  name: 'Augmented Reality',  meta: 'Unity · Mobile' },
    { icon: 'VR',  name: 'Virtual Reality',    meta: 'Immersive 3D' },
  ];
  return (
    <section className="pt-16 pb-[clamp(96px,14vh,160px)]">
      <div className="max-w-[1320px] mx-auto px-[clamp(20px,4vw,64px)]">
        <SectionHeader label="Toolbox" title="What I work with daily." counter="[ 08 capabilities ]" />
        <div className="reveal rd-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-borderc border border-borderc rounded-lg overflow-hidden">
          {skills.map((s, i) => (
            <div key={i} className="group bg-elev p-7 flex flex-col gap-[18px] transition-colors duration-200 hover:bg-surface">
              <div className="w-9 h-9 rounded-lg bg-surface border border-borderc-strong flex items-center justify-center font-mono text-sm text-accent font-semibold transition-all duration-200 group-hover:bg-accent group-hover:text-black group-hover:border-accent group-hover:-rotate-6">
                {s.icon}
              </div>
              <div>
                <div className="text-[17px] font-medium tracking-tight">{s.name}</div>
                <div className="font-mono text-[11px] text-ink-muted tracking-wide">{s.meta}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export { useReveal, Nav, Marquee, About, Skills, SectionHeader, SectionLabel, SectionTitle };
